// Contrato entre backend e painel. Campos em português; nenhum dado pessoal
// (nome, e-mail, telefone, documento) atravessa esta fronteira, com uma exceção:
// o e-mail do jogador na busca de jogadores (/v1/jogadores), decidida em 21/10/2026. O e-mail
// do afiliado só entra no pedido da busca de redes (/v1/afiliados/busca, 03/11/2026), nunca na resposta.

export * from './escolha.js';
export * from './conteudo.js';

/** Período inclusivo em datas locais (America/Sao_Paulo), AAAA-MM-DD. */
export interface Periodo {
  de: string;
  ate: string;
}

export interface QtdValor {
  quantidade: number;
  valor: number;
}

export interface Comparado<T> {
  atual: T;
  anterior: T;
}

export interface Erro {
  erro: string;
  detalhe?: string;
}

// ---------- Operações ----------

/**
 * Operações da Evox que o BI lê; cada uma tem réplica, usuário e schema próprios. A 99Play é a
 * principal: só ela tem site esportivo (tela Conteúdo) e carteira do afiliado (wallet-evox).
 */
export const OPERACOES = ['99play', 'trunfo-pt'] as const;
export type IdOperacao = (typeof OPERACOES)[number];

export const NOME_OPERACAO: Record<IdOperacao, string> = { '99play': '99Play', 'trunfo-pt': 'TRUNFO PT' };

/** Cabeçalho com que o painel diz de qual operação quer o dado; sem ele, vale a primeira do acesso. */
export const CABECALHO_OPERACAO = 'x-operacao';

/** Telas que só existem na 99Play. */
export const TELAS_SO_99PLAY: readonly Tela[] = ['conteudo', 'portal'];

// ---------- Acessos (/v1/eu) ----------

/**
 * Telas do painel; cada rota da API diz de quais telas ela é (backend/src/acessos.ts).
 * "portal" e "atendimento" não têm aba: são APIs de serviço (carteira do afiliado no
 * wallet-evox e pagamentos do jogador para o copiloto do atendimento).
 */
export const TELAS = ['visao-geral', 'pagamentos', 'afiliados', 'apostas', 'cassino', 'risco', 'saques', 'jogadores', 'niveis', 'conteudo', 'dados', 'acessos', 'portal', 'atendimento'] as const;
export type Tela = (typeof TELAS)[number];

export const PERFIS = ['admin', 'sportsbook', 'cassino', 'crm', 'afiliados', 'marketing', 'portal', 'atendimento'] as const;
export type Perfil = (typeof PERFIS)[number];

/** Telas que cada perfil abre. Quem tem mais de um perfil soma as telas. */
export const TELAS_DO_PERFIL: Record<Perfil, readonly Tela[]> = {
  admin: TELAS,
  sportsbook: ['visao-geral', 'apostas', 'risco', 'jogadores'],
  cassino: ['visao-geral', 'cassino', 'niveis', 'jogadores'],
  crm: ['visao-geral', 'pagamentos', 'apostas', 'cassino', 'jogadores', 'niveis'],
  afiliados: ['visao-geral', 'pagamentos', 'afiliados'],
  /** Edita o site esportivo (tela Conteúdo): banners, destaques e múltiplas. */
  marketing: ['visao-geral', 'conteudo'],
  /** Token de serviço do wallet-evox: só as rotas /v1/portal/*, nenhuma tela do painel. */
  portal: ['portal'],
  /** Token de serviço do copiloto do atendimento: acha o jogador e lê depósitos, saques e bilhetes dele. */
  atendimento: ['atendimento'],
};

/** Nome de um acesso: letras, números, espaço, ponto, hífen e sublinhado, até 40. */
export const NOME_ACESSO = /^[\p{L}\p{N} ._-]{1,40}$/u;

/** Telas de um conjunto de perfis, na ordem do menu. */
export const telasDosPerfis = (perfis: readonly Perfil[]): Tela[] =>
  TELAS.filter((t) => perfis.some((p) => TELAS_DO_PERFIL[p].includes(t)));

/** Quem entrou: o nome vem da configuração do acesso, não da Evox. */
export interface Eu {
  nome: string;
  perfis: Perfil[];
  telas: Tela[];
  /** Operações que o acesso vê, só as configuradas no servidor. Admin vê todas e troca pelo switch do topo. */
  operacoes: IdOperacao[];
}

// ---------- /v1/acessos (tela Acessos, só admin) ----------
// O token nunca volta numa listagem: só na criação e na troca, uma vez.

export interface AcessoCadastrado {
  id: string;
  nome: string;
  perfis: Perfil[];
  telas: Tela[];
  /** Operação que o acesso vê; null para admin, que vê todas. */
  operacao: IdOperacao | null;
  /** "painel": criado nesta tela, pode mudar. "servidor": BI_API_TOKEN e BI_ACESSOS, só leitura. */
  origem: 'painel' | 'servidor';
  /** ISO; null para os do servidor. */
  criadoEm: string | null;
  criadoPor: string | null;
  alteradoEm: string | null;
  alteradoPor: string | null;
}

export interface ListaAcessos {
  /** false quando o servidor não tem onde gravar (sem BI_ACESSOS_ARQUIVO nem StateDirectory). */
  gravavel: boolean;
  acessos: AcessoCadastrado[];
}

/** Corpo do POST /v1/acessos. */
export interface NovoAcesso {
  nome: string;
  perfis: Perfil[];
  /** Sem ela, 99Play. Ignorada para admin. */
  operacao?: IdOperacao;
}

/** Corpo do POST /v1/acessos/:id. Sem operação, mantém a atual. */
export interface AlteracaoAcesso {
  perfis: Perfil[];
  operacao?: IdOperacao;
}

/** Resposta da criação e da troca de token: o token aparece só aqui. */
export interface AcessoComToken {
  acesso: AcessoCadastrado;
  token: string;
}

// ---------- /v1/situacao ----------

export interface EstadoMetrica {
  nome: string;
  pronta: boolean;
  /** "tabela.coluna" que a métrica usa e o schema não tem. */
  faltando: string[];
}

export interface Situacao {
  geradoEm: string;
  /** Atraso da réplica da Evox em segundos; null quando o banco não informa. */
  atrasoReplicaSegundos: number | null;
  /** Último depósito visto (ISO), referência de "até quando o banco está completo". */
  ultimoDeposito: string | null;
  esquemaCarregadoEm: string;
  metricas: EstadoMetrica[];
}

// ---------- /v1/esquema ----------

export interface TabelaEsquema {
  nome: string;
  colunas: { nome: string; tipo: string }[];
}

export interface Esquema {
  schema: string;
  tabelas: TabelaEsquema[];
}

// ---------- /v1/resumo ----------

export interface IndicadoresResumo {
  cadastros: number;
  /** Primeiro depósito aprovado da vida do jogador caindo no período. */
  ftd: QtdValor;
  /** Todos os depósitos gerados (qualquer status). */
  depositosGerados: QtdValor;
  depositosPagos: QtdValor;
  depositantes: number;
  saquesPagos: QtdValor;
  /** Depósitos pagos − saques pagos. */
  liquido: number;
}

export interface Resumo {
  periodo: Periodo;
  periodoAnterior: Periodo;
  indicadores: Comparado<IndicadoresResumo>;
}

// ---------- /v1/pagamentos/por-dia ----------

export interface DiaPagamentos {
  dia: string;
  depositosGerados: QtdValor;
  depositosPagos: QtdValor;
  ftd: QtdValor;
  saquesPagos: QtdValor;
  liquido: number;
}

export interface PagamentosPorDia {
  periodo: Periodo;
  dias: DiaPagamentos[];
}

// ---------- /v1/afiliados ----------
// Afiliado só pelo id da conta e pelo id de jogador dela: nome e e-mail ficam fora.

/**
 * O que os jogadores indicados por afiliados fizeram no período. Conferido contra a visão geral
 * de afiliados da Evox em 25/10/2026: tudo igual, menos o GGR (0,26%) e os que depositaram.
 */
export interface IndicadoresAfiliados {
  /** Indicados que criaram a conta no período. */
  cadastros: number;
  /** Primeiro depósito aprovado da vida do indicado caindo no período. */
  ftd: QtdValor;
  depositosPagos: QtdValor;
  /** Indicados distintos com depósito pago (a Evox soma por rede e conta alguns duas vezes). */
  depositantes: number;
  /** Turnover: dinheiro real + bônus. */
  apostado: number;
  apostadoReal: number;
  apostadoBonus: number;
  /** Apostado − ganhos do cassino nos dias do período. */
  ggrCassino: number;
  /** Bilhetes de esportes liquidados no período: valor − prêmio − devolução. */
  ggrEsportes: number;
  ggr: number;
  /**
   * GGR menos a taxa da plataforma (admin_fee_pct do contrato de RevShare na Evox) de cada conta:
   * na rede, o GGR de cada subafiliado sai com a taxa dele, como a Evox faz. Taxa lida da API da
   * Evox com o valor de hoje, também para o período anterior. null quando a taxa não está à mão
   * (API desligada, fora do ar ou conta nova ainda sem contrato) e fora do portal.
   */
  ngr: number | null;
  /** Saques aprovados (pagos) dos indicados, pela data do pedido, como na tela Pagamentos. */
  saquesPagos: QtdValor;
  /** Depositado − sacado pelos indicados no período (o "NET" da ficha do afiliado na Evox). */
  net: number;
}

/** Uma conta de afiliado sem pai, somada com os subafiliados dela (árvore de parent_id). */
export interface RedeAfiliado extends IndicadoresAfiliados {
  /** Id interno da conta (afiliados.id). */
  conta: number;
  /** "ID do afiliado" de 5 dígitos que o time vê na Evox; null se a réplica não trouxer. */
  codigo: number | null;
  /** Nome do afiliado como está na Evox; null se a réplica não trouxer. Nada de e-mail ou documento. */
  nome: string | null;
  /** Id de jogador do dono da conta. */
  jogador: number | null;
  subafiliados: number;
  /** Jogadores indicados pela rede desde sempre. */
  indicados: number;
}

// ---------- POST /v1/afiliados/busca ----------
// O e-mail do afiliado vai no corpo do POST (nunca na URL ou em log) e não volta na resposta;
// a rota não passa pelo cache.

/** Corpo do POST: e-mail completo de uma conta de afiliado. */
export interface BuscaAfiliado {
  termo: string;
}

export interface ResultadoBuscaAfiliado {
  /** Conta raiz da rede de cada conta com esse e-mail (a própria, se ela não tiver conta acima). */
  redes: number[];
}

export interface Afiliados {
  periodo: Periodo;
  periodoAnterior: Periodo;
  indicadores: Comparado<IndicadoresAfiliados>;
  /** Do maior GGR para o menor; inclui as redes sem movimento. */
  redes: RedeAfiliado[];
  /** Contas de afiliado, com subafiliados. */
  contas: number;
}

// ---------- /v1/portal/* (carteira do afiliado, wallet-evox) ----------
// Conta = afiliados.id da réplica. Nada de nome, e-mail ou documento: o portal guarda o que
// o time digitou no convite. Jogador só como contagem.

export interface ContaPortal {
  conta: number;
  /** Conta acima desta; null quando ela é a raiz de uma rede. */
  pai: number | null;
  /** Id de jogador do dono da conta. */
  jogador: number | null;
  /** ISO. */
  criadaEm: string | null;
  /** Todas as contas debaixo desta, em qualquer nível. */
  subafiliados: number;
  /** Jogadores indicados pela rede desta conta desde sempre. */
  indicados: number;
  /** Situação da conta na Evox (active, paused…); null se a réplica não tiver a coluna. */
  situacao: string | null;
  /**
   * Token do link de divulgação (?ref=) do dono da conta: o recommendation_token do jogador.
   * Não é dado pessoal: vai em todo link que o afiliado publica.
   */
  token: string | null;
  /** O afiliado já cadastrou a chave Pix na Evox (a chave nunca sai do BI). */
  pixCadastrado: boolean;
}

/**
 * Primeiro acesso por e-mail (POST /v1/portal/primeiro-acesso): o afiliado digita o e-mail na
 * carteira e ela pergunta de quem é. O e-mail só entra, no corpo do POST; nunca sai nem vai ao log.
 */
export interface PedidoPrimeiroAcesso {
  email: string;
}

/** Conta de afiliado cujo dono tem o e-mail pedido. Código e nome como na aba Afiliados. */
export interface ContaDoEmail {
  conta: number;
  codigo: number | null;
  nome: string | null;
  /** active, paused… como a Evox grava; null se a réplica não tiver a coluna. */
  situacao: string | null;
}

export interface PrimeiroAcessoPortal {
  /** Vazia quando o e-mail não é de afiliado. Mais de uma: o mesmo dono tem várias contas. */
  contas: ContaDoEmail[];
}

/** "rede": a conta e todos os subafiliados debaixo dela; "propria": só os indicados direto pela conta. */
export const RECORTES_PORTAL = ['rede', 'propria'] as const;
export type RecortePortal = (typeof RECORTES_PORTAL)[number];

/** Um subafiliado direto, somado com a rede dele. */
export interface SubafiliadoPortal extends IndicadoresAfiliados {
  conta: number;
  subafiliados: number;
  situacao: string | null;
}

export interface IndicadoresPortal {
  conta: number;
  recorte: RecortePortal;
  periodo: Periodo;
  periodoAnterior: Periodo;
  indicadores: Comparado<IndicadoresAfiliados>;
  /** Subafiliados diretos no período, do maior GGR para o menor (sempre pela rede de cada um). */
  subafiliados: SubafiliadoPortal[];
}

/** Um dia do período, na rede da conta ou só nos indicados dela. Dia sem movimento vem zerado. */
export interface DiaPortal {
  /** AAAA-MM-DD, no fuso de Brasília. */
  dia: string;
  cadastros: number;
  ftd: QtdValor;
  depositosPagos: QtdValor;
  saquesPagos: QtdValor;
  apostado: number;
  ggr: number;
}

/** Os mesmos indicadores de IndicadoresPortal, dia a dia: a soma dos dias dá o período. */
export interface SeriePortal {
  conta: number;
  recorte: RecortePortal;
  periodo: Periodo;
  /** Todos os dias do período, do mais antigo para o mais novo. */
  dias: DiaPortal[];
}

export interface ComissaoAfiliado {
  id: number;
  /** ISO. */
  criadaEm: string;
  /** revshare, cpa, deposit_pct, adjustment… como a Evox grava. */
  modelo: string;
  base: number | null;
  /** Percentual como a Evox grava. Null quando ela grava 0, como em todo RevShare. */
  taxa: number | null;
  valor: number;
  /** available conta no saldo; os outros, não. */
  situacao: string;
  /** Ajuste com source_ref "advance:<id>": adiantamento a descontar, não comissão. */
  adiantamento: boolean;
  /** De que pedaço do mês é o lançamento de RevShare. Null nos outros lançamentos. */
  revshare: RevshareLancamento | null;
}

/**
 * Lançamento de RevShare da Evox. O ciclo ("rev:AAAA-MM:AAAA-MM-DDTHH") paga a base desde o corte
 * anterior do mês; o fechamento ("rev:AAAA-MM") traz a base do mês inteiro e paga o resultado do
 * mês menos o que os ciclos já creditaram.
 */
export interface RevshareLancamento {
  tipo: 'ciclo' | 'fechamento';
  /** AAAA-MM. */
  mes: string;
  /** Dias cobertos, inclusivos, no fuso de Brasília. Null no fechamento que não pegou dia novo. */
  de: string | null;
  ate: string | null;
  /** Só no fechamento: soma dos ciclos disponíveis do mesmo mês. */
  jaCreditado: number | null;
}

export interface SaqueAfiliado {
  id: number;
  /** ISO. */
  pedidoEm: string;
  valor: number;
  metodo: string | null;
  situacao: string;
  observacao: string | null;
  /** ISO. */
  revisadoEm: string | null;
}

export interface SaldoAfiliado {
  /** Comissões disponíveis − adiantamentos − saques que não foram recusados nem cancelados. */
  disponivel: number;
  /** Comissões com situação available, sem adiantamentos. */
  comissoes: number;
  /** Adiantamentos disponíveis, em valor positivo (já descontados do saldo). */
  adiantamentos: number;
  saquesEmAndamento: number;
  saquesPagos: number;
}

export interface CarteiraAfiliado {
  conta: number;
  saldo: SaldoAfiliado;
  /** Últimos 100 lançamentos, do mais novo para o mais antigo. */
  comissoes: ComissaoAfiliado[];
  /** Últimos 50 saques, do mais novo para o mais antigo. */
  saques: SaqueAfiliado[];
}

// ---------- POST /v1/afiliados/:conta/links (aba Afiliados) ----------

export const TIPOS_LINK_PORTAL = ['convite', 'ver-como'] as const;
export type TipoLinkPortal = (typeof TIPOS_LINK_PORTAL)[number];

/** Convite exige o código de 5 dígitos e o nome copiados do painel da Evox. */
export interface PedidoLinkPortal {
  tipo: TipoLinkPortal;
  codigo?: number;
  nome?: string;
}

export interface LinkPortal {
  tipo: TipoLinkPortal;
  conta: number;
  /** O token vai no fragmento (#), que não chega ao servidor nem aos logs. */
  url: string;
  /** ISO. */
  expiraEm: string;
}

// ---------- /v1/afiliados/saques (fila de saques pagos pela carteira) ----------
// O BI só repassa à API de admin do wallet-evox (/api/admin/saques). A chave Pix vai inteira
// porque o time paga por ela (exceção de 30/10/2026): nunca no log nem no cache.

export const FILTROS_SAQUES_CARTEIRA = ['abertos', 'pagos', 'recusados'] as const;
export type FiltroSaquesCarteira = (typeof FILTROS_SAQUES_CARTEIRA)[number];

export const ACOES_SAQUE_CARTEIRA = ['aprovar', 'recusar', 'pagar'] as const;
export type AcaoSaqueCarteira = (typeof ACOES_SAQUE_CARTEIRA)[number];

export type TipoChavePix = 'cpf' | 'cnpj' | 'email' | 'telefone' | 'aleatoria';

export interface SaqueCarteira {
  id: number;
  marca: string;
  conta: number;
  /** Código de 5 dígitos e nome do afiliado, do convite; null se a conta não tem acesso. */
  codigo: number | null;
  nome: string | null;
  valor: number;
  pixTipo: TipoChavePix;
  /** CPF e CNPJ só dígitos; telefone +55DDDNÚMERO. A chave do momento do pedido. */
  pixChave: string;
  /** pending → approved → paid; rejected (time) e cancelled (afiliado). */
  situacao: string;
  /** ISO. */
  pedidoEm: string;
  decididoEm: string | null;
  decididoPor: string | null;
  /** Motivo da recusa; o afiliado vê. */
  motivo: string | null;
  pagoEm: string | null;
  pagoPor: string | null;
  /** Identificador do Pix (E2E) informado pelo time. */
  comprovante: string | null;
}

export interface ListaSaquesCarteira {
  saques: SaqueCarteira[];
}

export interface DecisaoSaqueCarteira {
  acao: AcaoSaqueCarteira;
  /** Obrigatório para recusar (3 a 200 caracteres). */
  motivo?: string;
  /** Obrigatório para marcar como pago. */
  comprovante?: string;
}

// ---------- /v1/apostas/por-dia ----------

/** Apostas de um segmento (cassino, esportes ou os dois juntos) num dia ou no período. */
export interface Apostas {
  apostado: number;
  ganho: number;
  /** Vem negativo nos ajustes de esportes; entra com o sinal no GGR. */
  estorno: number;
  /** apostado − ganho − estorno. */
  ggr: number;
  jogadores: number;
}

export type Segmento = 'total' | 'cassino' | 'esportes';

export type ApostasPorSegmento = Record<Segmento, Apostas>;

export interface DiaApostas extends ApostasPorSegmento {
  dia: string;
}

export interface ApostasPorDia {
  periodo: Periodo;
  dias: DiaApostas[];
  total: ApostasPorSegmento;
}

// ---------- /v1/cassino/jogos ----------

/** Desempenho no cassino (sem as apostas esportivas) de um jogo, de um provedor ou do período. */
export interface DesempenhoCassino {
  apostado: number;
  ganho: number;
  estorno: number;
  /** apostado − ganho − estorno. */
  ggr: number;
  /** Apostas (rodadas) registradas pelo provedor. */
  rodadas: number;
  jogadores: number;
}

export interface JogoCassino extends DesempenhoCassino {
  jogo: string;
  provedor: string;
}

export interface ProvedorCassino extends DesempenhoCassino {
  provedor: string;
  /** Jogos do provedor com movimento no período. */
  jogos: number;
}

export interface JogosCassino {
  periodo: Periodo;
  total: DesempenhoCassino & { jogos: number; provedores: number };
  provedores: ProvedorCassino[];
  /** Todos os jogos com movimento no período, do mais apostado para o menos. */
  jogos: JogoCassino[];
}

// ---------- /v1/cassino/jogo ----------

/** Um jogador num jogo do cassino, no período. Só o id: nenhum dado pessoal. */
export interface JogadorNoJogo extends Omit<DesempenhoCassino, 'jogadores'> {
  jogador: number;
  /** Dias do período em que jogou o jogo. */
  dias: number;
}

/** Médias por jogador (e por rodada) de um jogo no período. */
export interface MediasJogoCassino {
  apostadoPorJogador: number;
  /** Metade dos jogadores apostou até este valor. */
  medianaApostado: number;
  rodadasPorJogador: number;
  /** Apostado ÷ rodadas: o valor típico de cada aposta. */
  apostaPorRodada: number;
  ggrPorJogador: number;
  diasPorJogador: number;
}

/** Faixa de quanto cada jogador apostou no jogo. */
export interface FaixaJogadores {
  faixa: string;
  jogadores: number;
  apostado: number;
}

export interface DiaJogoCassino {
  dia: string;
  apostado: number;
  ggr: number;
  rodadas: number;
  jogadores: number;
}

export interface DetalheJogoCassino {
  periodo: Periodo;
  jogo: string;
  provedor: string;
  total: DesempenhoCassino & { dias: number };
  medias: MediasJogoCassino;
  /** Jogadores que ganharam mais do que apostaram (GGR negativo para a casa). */
  jogadoresNoLucro: number;
  /** Fatia do apostado que veio dos 10 jogadores que mais apostaram (0 a 1). */
  concentracaoTop10: number;
  faixas: FaixaJogadores[];
  dias: DiaJogoCassino[];
  /** Os que mais apostaram (até LIMITE_JOGADORES_JOGO); total.jogadores diz quantos são ao todo. */
  jogadores: JogadorNoJogo[];
}

export const LIMITE_JOGADORES_JOGO = 100;

/** Critério dos rankings da aba Cassino. */
export type RankingCassino = 'apostado' | 'rodadas' | 'jogadores' | 'ggr';

// ---------- /v1/niveis ----------

/**
 * Um degrau da trilha. Jogadores é foto de agora (pelo XP); depósitos e apostado são do
 * período, só de quem depositou nele.
 */
export interface DegrauNivel {
  nome: string;
  xpMinimo: number;
  jogadores: number;
  depositantes: number;
  depositos: number;
  depositado: number;
  apostado: number;
}

/** Valor da classificação do CRM (faixa, estágio ou sinal) com a contagem de agora. */
export interface ItemClassificacao {
  chave: string;
  rotulo: string;
  regra: string;
  jogadores: number;
}

export interface FaixaValor extends ItemClassificacao {
  /** Menor e maior depósito acumulado (vida toda) dos jogadores da faixa; null sem depósito. */
  depositoMin: number | null;
  depositoMax: number | null;
}

export type MedidaConcentracao = 'apostado' | 'ggr' | 'depositado';

/** Parcela do total do período que vem dos N maiores jogadores (0 a 1; GGR pode passar de 1). */
export interface ConcentracaoTopo {
  medida: MedidaConcentracao;
  total: number;
  topos: { maiores: number; parcela: number }[];
}

export interface Niveis {
  periodo: Periodo;
  /** Jogadores cadastrados: a base das porcentagens dos degraus. */
  base: number;
  degraus: DegrauNivel[];
  checkin: { comSequencia: number; mediaSequencia: number; maiorSequencia: number };
  /** Quando a Evox recalculou a classificação (ISO); null se não informa. */
  classificadoEm: string | null;
  classificados: number;
  faixas: FaixaValor[];
  estagios: ItemClassificacao[];
  sinais: ItemClassificacao[];
  concentracao: ConcentracaoTopo[];
}

// ---------- /v1/risco/esportes ----------

/** simples = uma escolha num jogo; betbuilder = várias escolhas no mesmo jogo; multipla = mais de um jogo. */
export type TipoBilhete = 'simples' | 'betbuilder' | 'multipla';

/** Bilhetes ainda sem resultado e quanto eles pagam se todas as escolhas baterem. */
export interface RiscoAberto {
  bilhetes: number;
  jogadores: number;
  /** Só dinheiro; aposta grátis não entra. */
  apostado: number;
  pagaSeBater: number;
  /** pagaSeBater − apostado: o que a casa perde se tudo bater. */
  perdaSeBater: number;
  /** Maior pagamento possível de um único bilhete. */
  maiorBilhete: number;
}

export interface RiscoFechado {
  bilhetes: number;
  apostado: number;
  pago: number;
  devolvido: number;
  /** apostado − pago − devolvido. Positivo = a casa ganhou. */
  resultado: number;
  /** Status como vem da Evox (won, lost, void, cashout...). */
  porStatus: { status: string; bilhetes: number }[];
}

export interface JogoRisco {
  /** Nome do jogo como vem no bilhete ("Time A vs. Time B"); cruza com /v1/ao-vivo pelo nome. */
  jogo: string;
  /** Esporte e início só vêm nos bilhetes que não são da Altenar. */
  esporte: string | null;
  /** Início do jogo (ISO). */
  inicio: string | null;
  /** Todos os bilhetes do dia que passam pelo jogo, abertos e fechados. */
  bilhetes: number;
  /** Desses, os que ainda não têm resultado. */
  abertos: number;
  /**
   * GGR dos bilhetes do jogo que já fecharam: apostado − pago − devolvido. Positivo, a casa
   * ganhou. A múltipla conta inteira em cada jogo que toca.
   */
  ggrFechado: number;
  /** Jogadores com bilhete aberto no jogo. */
  jogadores: number;
  /** Valor inteiro dos bilhetes abertos que passam pelo jogo; múltipla conta inteira em cada jogo. */
  apostado: number;
  /** Paga se bater, por tipo de bilhete. Múltipla entra com o pagamento inteiro (teto). */
  simples: number;
  betbuilder: number;
  multiplas: number;
  pagaSeBater: number;
}

export interface BilheteAberto {
  bilhete: string;
  /** Só o id; nada de nome ou contato. */
  jogador: number;
  provedor: string;
  tipo: TipoBilhete;
  selecoes: number;
  apostado: number;
  freebet: boolean;
  odd: number;
  pagaSeBater: number;
  descricao: string | null;
  feitoEm: string;
}

export interface JogadorAberto {
  jogador: number;
  bilhetes: number;
  apostado: number;
  pagaSeBater: number;
}

/** Bilhetes abertos com exatamente as mesmas escolhas (ex.: 400 bilhetes iguais de 50 jogadores). */
export interface PadraoBilhete {
  /** Identifica o padrão no dia; abre o detalhe em /v1/risco/esportes/padrao. */
  id: string;
  /** "Jogo : Mercado: Escolha", em ordem alfabética. */
  escolhas: string[];
  bilhetes: number;
  jogadores: number;
  apostado: number;
  oddMedia: number;
  pagaSeBater: number;
}

/**
 * Situação de uma escolha, deduzida de outros bilhetes já liquidados com ela: um bilhete
 * ganho (simples ou múltipla) mostra que bateu; uma simples perdida, que não bateu.
 */
export type SituacaoEscolha = 'bateu' | 'nao-bateu' | 'anulada' | 'sem-resultado';

export interface EscolhaPadrao {
  escolha: string;
  jogo: string;
  situacao: SituacaoEscolha;
  /** Bilhetes liquidados que levaram à situação. */
  ganhos: number;
  simplesPerdidas: number;
  simplesAnuladas: number;
}

export interface DetalhePadrao extends PadraoBilhete {
  dia: string;
  geradoEm: string;
  escolhasSituacao: EscolhaPadrao[];
  /** Todos os jogadores do padrão, do que mais recebe para o que menos recebe. */
  jogadoresPadrao: JogadorAberto[];
  bilhetesPadrao: BilheteAberto[];
}

/** Uma faixa de 15 minutos do dia (horário de Brasília) e a mesma faixa no dia anterior. */
export interface PontoMovimento {
  /** "14:15": início da faixa. */
  faixa: string;
  /** null nas faixas que ainda não chegaram (dia de hoje). */
  bilhetes: number | null;
  apostado: number | null;
  /** Apostado desde a meia-noite até o fim da faixa. */
  acumulado: number | null;
  anteriorBilhetes: number;
  anteriorApostado: number;
  anteriorAcumulado: number;
}

export interface MovimentoDia {
  /** Dia usado na comparação (o anterior ao escolhido). */
  anterior: string;
  pontos: PontoMovimento[];
}

export interface RiscoEsportes {
  /** Dia local (America/Sao_Paulo) em que os bilhetes foram feitos. */
  dia: string;
  geradoEm: string;
  feitas: { bilhetes: number; jogadores: number; apostado: number; freebet: QtdValor };
  fechadas: RiscoFechado;
  abertas: RiscoAberto & { porTipo: Record<TipoBilhete, RiscoAberto> };
  /** Apostado no dia − pago − devolvido − o que os abertos pagam se baterem. */
  piorResultado: number;
  /** Jogos com bilhete aberto, do que mais paga para o que menos paga (até 1000). */
  jogos: JogoRisco[];
  totalJogos: number;
  maioresBilhetes: BilheteAberto[];
  maioresJogadores: JogadorAberto[];
  /** Padrões com 3 ou mais bilhetes abertos iguais, dos mais repetidos (até 50). */
  padroes: PadraoBilhete[];
  /** Bilhetes e apostado de 15 em 15 minutos, contra o dia anterior. */
  movimento: MovimentoDia;
}

// ---------- /v1/ao-vivo ----------

export interface JogoAoVivo {
  /** Id do evento na Altenar. */
  jogo: string;
  /** "Time A vs. Time B", o mesmo formato dos bilhetes. */
  evento: string;
  /** "2–1" */
  placar: string | null;
  /** Minuto ou fase, como a Altenar mostra ("46'", "Intervalo"). */
  tempo: string | null;
}

// ---------- /v1/risco/bilhete e /v1/jogadores/:id/bilhetes ----------

/** Um bilhete da Altenar com o resultado, aberto ou já liquidado. */
export interface BilheteCompleto {
  bilhete: string;
  /** Só o id; o perfil abre pela busca de jogadores. */
  jogador: number;
  /** Como vem da Evox: open, won, lost, cashout, void. */
  status: string;
  aberto: boolean;
  apostado: number;
  odd: number;
  pagaSeBater: number;
  pago: number;
  devolvido: number;
  /** apostado − pago − devolvido, do ponto de vista da casa; null enquanto aberto. */
  resultado: number | null;
  /** Escolhas como a Evox grava ("Jogo : Mercado: Escolha"), na ordem do bilhete. */
  escolhas: string[];
  feitoEm: string;
  liquidadoEm: string | null;
}

export interface JogadorSimilar {
  jogador: number;
  bilhetes: number;
  abertos: number;
  apostado: number;
  /** Só dos bilhetes ainda abertos. */
  pagaSeBater: number;
  pago: number;
}

/** Um bilhete e todos os bilhetes do mesmo dia com exatamente as mesmas escolhas. */
export interface SimilaresBilhete {
  geradoEm: string;
  /** Dia (Brasília) em que o bilhete foi feito; os iguais são desse dia. */
  dia: string;
  bilhete: BilheteCompleto;
  /** Inclui o próprio bilhete; do maior pagamento possível para o menor (até 2000). */
  iguais: BilheteCompleto[];
  jogadores: JogadorSimilar[];
  totais: { bilhetes: number; abertos: number; jogadores: number; apostado: number; pagaSeBater: number; pago: number; resultado: number };
}

export interface BilhetesDoJogador {
  jogador: number;
  periodo: Periodo;
  /** Os mais recentes primeiro (até 100). */
  bilhetes: BilheteCompleto[];
  totais: { bilhetes: number; abertos: number; apostado: number; pago: number; resultado: number };
}

// ---------- /v1/risco/esportes/padrao/estatisticas ----------

/** [casa, fora]. */
export type Placar = [number, number];

export interface NumerosPorTempo {
  jogo: Placar | null;
  primeiroTempo: Placar | null;
  segundoTempo: Placar | null;
}

/**
 * Como a escolha está pelo placar agora: bateu/não bateu já decididos; a favor/contra
 * se o jogo terminasse agora; falta quando ainda precisa acontecer mais.
 */
export type ResultadoPerna = 'bateu' | 'nao-bateu' | 'a-favor' | 'contra' | 'falta' | 'sem-avaliacao';

export interface AvaliacaoPerna {
  escolha: string;
  mercado: string;
  resultado: ResultadoPerna;
  /** Explicação curta com os números ("faltam 2: 4 de 6 escanteios"). */
  texto: string;
}

export interface EstatisticasJogo {
  /** Texto original da escolha do padrão. */
  escolha: string;
  jogo: string;
  /** Id do evento na Altenar, quando o jogo está ao vivo. */
  evento: string | null;
  disponivel: boolean;
  /** Por que não há números (jogo fora do ao vivo, Altenar fora do ar). */
  motivo?: string;
  tempo: string | null;
  fase: string | null;
  casa: string | null;
  fora: string | null;
  gols: NumerosPorTempo;
  escanteios: NumerosPorTempo;
  amarelos: NumerosPorTempo;
  vermelhos: NumerosPorTempo;
  pernas: AvaliacaoPerna[];
}

export interface EstatisticasPadrao {
  geradoEm: string;
  jogos: EstatisticasJogo[];
}

// ---------- /v1/altenar/eventos ----------

export interface TimeAltenar {
  id: number;
  nome: string;
  /** URL pública do escudo na Altenar. */
  escudo: string;
}

/** Um jogo do catálogo da Altenar (ao vivo ou próximos), para horário e escudos na tabela. */
export interface EventoAltenar {
  evento: string;
  /** "Time A vs. Time B", o mesmo formato dos bilhetes. */
  nome: string;
  inicio: string | null;
  esporte: string | null;
  campeonato: string | null;
  casa: TimeAltenar | null;
  fora: TimeAltenar | null;
  aoVivo: boolean;
}

export interface CatalogoEventos {
  geradoEm: string;
  disponivel: boolean;
  motivo?: string;
  eventos: EventoAltenar[];
}

// ---------- /v1/risco/jogo ----------

/** Uma escolha (mercado e palpite) de um jogo e quanto ela expõe a casa. */
export interface EscolhaDoJogo {
  /** "Mercado: Palpite", sem o nome do jogo. */
  escolha: string;
  bilhetes: number;
  jogadores: number;
  /** Paga se bater, por tipo de bilhete; múltipla entra inteira (teto). */
  simples: number;
  betbuilder: number;
  multiplas: number;
  pagaSeBater: number;
  apostado: number;
  /** Como está pelo placar ao vivo agora, quando o jogo está rolando. */
  avaliacao?: AvaliacaoPerna[];
}

export interface DetalheJogo {
  geradoEm: string;
  dia: string;
  jogo: string;
  evento: EventoAltenar | null;
  totais: {
    bilhetes: number;
    abertos: number;
    jogadores: number;
    apostado: number;
    pagaSeBater: number;
    /** Apostado − pago − devolvido nos bilhetes do jogo já fechados. */
    ggrFechado: number;
  };
  porTipo: Record<TipoBilhete, { bilhetes: number; pagaSeBater: number }>;
  /** Escolhas abertas do jogo, da que mais paga para a que menos paga (até 100). */
  escolhas: EscolhaDoJogo[];
  maioresBilhetes: BilheteAberto[];
  maioresJogadores: JogadorAberto[];
  /** Placar detalhado, quando o jogo está ao vivo. */
  placar: EstatisticasJogo | null;
}

// ---------- /v1/risco/resultado ----------

/**
 * Recorte do resultado do dia: `feitos` são os bilhetes feitos no dia que já fecharam (bate com
 * o "Já fechado" da tela); `liquidados` são os bilhetes que fecharam no dia, feitos nele ou antes
 * (o caixa do dia, como o GGR da Evox).
 */
export type BaseResultado = 'feitos' | 'liquidados';

/** Bilhetes fechados e o que a casa fez com eles. */
export interface ResultadoFechado {
  bilhetes: number;
  /** Só dinheiro; aposta grátis não entra. */
  apostado: number;
  pago: number;
  devolvido: number;
  /** apostado − pago − devolvido. Positivo = a casa ganhou. */
  resultado: number;
}

export interface JogoResultado extends ResultadoFechado {
  jogo: string;
  /** Esporte só vem nos bilhetes que não são da Altenar. */
  esporte: string | null;
  /** Jogadores com bilhete fechado no jogo. */
  jogadores: number;
  /** Bilhetes do jogo que ainda não fecharam (só no recorte por dia da aposta; senão 0). */
  abertos: number;
  /**
   * Resultado por tipo. A múltipla entra dividida em partes iguais pelos jogos do bilhete, para a
   * soma dos jogos fechar com o total do dia (apostado, pago e devolvido também vêm rateados).
   */
  simples: number;
  betbuilder: number;
  multiplas: number;
  /** Bilhetes fechados do jogo que pagaram (ganho ou cash-out) e o maior prêmio entre eles (inteiro). */
  premiados: number;
  maiorPremio: number;
}

export interface BilheteFechado {
  bilhete: string;
  /** Só o id; nada de nome ou contato. */
  jogador: number;
  provedor: string;
  tipo: TipoBilhete;
  selecoes: number;
  /** Status como vem da Evox (won, lost, void, cashout...). */
  status: string;
  apostado: number;
  freebet: boolean;
  odd: number;
  pago: number;
  devolvido: number;
  /** apostado − pago − devolvido. */
  resultado: number;
  descricao: string | null;
  feitoEm: string;
  liquidadoEm: string | null;
}

export interface JogadorResultado {
  jogador: number;
  bilhetes: number;
  apostado: number;
  pago: number;
  /** Resultado da casa com o jogador: negativo, o jogador ganhou. */
  resultado: number;
}

export interface ResultadoDia {
  dia: string;
  base: BaseResultado;
  geradoEm: string;
  totais: ResultadoFechado & { jogadores: number; porTipo: Record<TipoBilhete, ResultadoFechado> };
  porStatus: { status: string; bilhetes: number; pago: number }[];
  /** Todos os jogos com bilhete fechado (até 2000), do pior resultado para o melhor. */
  jogos: JogoResultado[];
  totalJogos: number;
  /** Bilhetes fechados sem jogo legível no texto: entram no total, não na lista de jogos. */
  semJogo: ResultadoFechado;
  /** Bilhetes que mais pagaram (até 20). */
  maioresPremios: BilheteFechado[];
  /** Jogadores com o pior resultado para a casa (até 20). */
  jogadoresQueMaisGanharam: JogadorResultado[];
}

// ---------- /v1/risco/eventos (betlist) ----------

/** Um evento encontrado na busca pelos bilhetes dos últimos dias. */
export interface EventoEncontrado {
  /** Nome como vem no bilhete ("Time A vs. Time B"). */
  jogo: string;
  esporte: string | null;
  bilhetes: number;
  abertos: number;
  /** Apostado inteiro nos bilhetes que passam pelo jogo. */
  apostado: number;
  /** apostado − pago − devolvido nos bilhetes já fechados (múltipla inteira). */
  resultadoFechado: number;
  ultimaAposta: string;
}

export interface BuscaEventos {
  termo: string;
  /** Dias locais das apostas lidas (inclusivos). */
  de: string;
  ate: string;
  /** Do evento com mais bilhetes para o com menos (até 30). */
  eventos: EventoEncontrado[];
}

/** Indicadores de um conjunto de bilhetes do evento (todos, um tipo, um jogador). */
export interface IndicadoresEvento {
  bilhetes: number;
  abertos: number;
  jogadores: number;
  /** Só dinheiro, abertos e fechados. */
  apostado: number;
  /** Fechados: o que foi pago e devolvido. */
  pago: number;
  devolvido: number;
  /** apostado − pago − devolvido nos fechados. Positivo = a casa ganhou. */
  resultadoFechado: number;
  /** Abertos: o apostado e o que pagam se baterem. */
  apostadoAberto: number;
  pagaSeBater: number;
  /** resultadoFechado + apostadoAberto − pagaSeBater: o que a casa fecha se todos os abertos baterem. */
  piorResultado: number;
}

/** Uma escolha (mercado e palpite) do evento, abertos e fechados. */
export interface EscolhaEvento {
  /** "Mercado: Palpite", sem o nome do jogo. */
  escolha: string;
  situacao: SituacaoEscolha;
  bilhetes: number;
  jogadores: number;
  apostado: number;
  ganhos: number;
  perdidos: number;
  abertos: number;
  /** Pago nos bilhetes ganhos que têm a escolha (múltipla inteira). */
  pago: number;
  /** O que os abertos com a escolha pagam se baterem. */
  pagaSeBater: number;
}

/** Linha da betlist: um bilhete que passa pelo evento. */
export interface BilheteEvento {
  bilhete: string;
  /** Só o id; nada de nome ou contato. */
  jogador: number;
  provedor: string;
  tipo: TipoBilhete;
  /** PreLive, Live, Mixed ou VSPORT (só Altenar). */
  modo: string | null;
  selecoes: number;
  status: string;
  aberto: boolean;
  apostado: number;
  freebet: boolean;
  odd: number;
  pagaSeBater: number;
  pago: number;
  devolvido: number;
  /** apostado − pago − devolvido; null enquanto aberto. */
  resultado: number | null;
  descricao: string | null;
  feitoEm: string;
  liquidadoEm: string | null;
}

export interface JogadorEvento {
  jogador: number;
  bilhetes: number;
  abertos: number;
  apostado: number;
  pago: number;
  resultadoFechado: number;
  pagaSeBater: number;
}

export interface DetalheEvento {
  geradoEm: string;
  jogo: string;
  de: string;
  ate: string;
  /** Horário, campeonato e escudos, do catálogo da Altenar (só jogos que ainda vão começar ou estão rolando). */
  evento: EventoAltenar | null;
  aoVivo: JogoAoVivo | null;
  /** Todos os bilhetes que passam pelo jogo; a múltipla conta inteira. */
  totais: IndicadoresEvento;
  /** Só a parte do jogo: múltipla dividida em partes iguais pelos jogos do bilhete. */
  resultadoRateado: number;
  porTipo: Record<TipoBilhete, IndicadoresEvento>;
  porStatus: { status: string; bilhetes: number; apostado: number; pago: number }[];
  /** Apostado por dia da aposta. */
  porDia: { dia: string; bilhetes: number; apostado: number }[];
  /** Escolhas do evento, da mais apostada para a menos (até 300). */
  escolhas: EscolhaEvento[];
  /** Jogadores, do pior resultado para a casa ao melhor (até 300). */
  jogadores: JogadorEvento[];
  /** Os bilhetes, do mais recente para o mais antigo (até 3000). */
  bilhetes: BilheteEvento[];
}

/** Jogos ao vivo agora na Altenar. Fora do ar não derruba a tela: vem com disponivel = false. */
export interface AoVivo {
  geradoEm: string;
  disponivel: boolean;
  motivo?: string;
  jogos: JogoAoVivo[];
}

// ---------- POST /v1/jogadores/busca e GET /v1/jogadores/:id ----------
// Única parte do contrato com dado pessoal: o e-mail. Ele vai no corpo do POST
// (nunca na URL) e as duas rotas não passam pelo cache.

/** Corpo do POST: id (só números) ou e-mail completo. */
export interface BuscaJogador {
  termo: string;
}

export interface JogadorEncontrado {
  id: number;
  email: string | null;
  /** Instante ISO-8601 (UTC). */
  cadastro: string | null;
}

export interface ResultadoBusca {
  jogadores: JogadorEncontrado[];
}

/** Apostas de um jogador num segmento; sem contagem de jogadores. */
export type ApostasDoJogador = Omit<Apostas, 'jogadores'>;

export interface PagamentosJogador {
  depositosPagos: QtdValor;
  saquesPagos: QtdValor;
  /** Depósitos pagos menos saques pagos. */
  liquido: number;
  /** Instantes ISO-8601 (UTC) do primeiro e do último depósito pago. */
  primeiroDeposito: string | null;
  ultimoDeposito: string | null;
}

export interface ClassificacaoJogador {
  /** Estágio do CRM da Evox (ex.: ftd_recente). */
  estagio: string | null;
  /** Faixa de valor (ex.: baleia). */
  faixa: string | null;
  marcas: string[];
  ultimaAtividade: string | null;
}

export interface JogoPreferido {
  jogo: string;
  segmento: Exclude<Segmento, 'total'>;
  apostado: number;
  ganho: number;
  ggr: number;
  /** Dias com aposta nesse jogo. */
  dias: number;
}

/** Linha do extrato da carteira ligada a um jogo: débito da aposta ou crédito do ganho. */
export interface MovimentoAposta {
  em: string;
  tipo: 'aposta' | 'ganho';
  segmento: Exclude<Segmento, 'total'>;
  jogo: string;
  provedor: string | null;
  valor: number;
}

export interface ResumoJogador {
  id: number;
  email: string | null;
  cadastro: string | null;
  banido: boolean;
  /** Último saldo real; null quando a Evox não tem saldo para o jogador. */
  saldo: number | null;
  classificacao: ClassificacaoJogador | null;
  /** Desde o cadastro. */
  pagamentos: PagamentosJogador;
  apostas: {
    periodo: Periodo;
    noPeriodo: Record<Segmento, ApostasDoJogador>;
    desdeOCadastro: Record<Segmento, ApostasDoJogador>;
  };
  /** Até 8 jogos, por valor apostado desde o cadastro. */
  jogosPreferidos: JogoPreferido[];
  /** Até 30 movimentos, do mais recente para o mais antigo. */
  ultimasApostas: MovimentoAposta[];
}

// ---------- /v1/risco/liability ----------

/**
 * Recorte pelo jogo, como o filtro "Prelive/Live" da Altenar: pré-jogo são os jogos que ainda não
 * começaram e começam nas próximas 24 horas; ao vivo, os em andamento; todos inclui os já
 * encerrados que ainda seguram múltiplas abertas.
 */
export type ModoLiability = 'pre-jogo' | 'ao-vivo' | 'todos';

/** Bilhetes e apostado de um tipo numa escolha (ou mercado). */
export interface ApostasTipo {
  bilhetes: number;
  apostado: number;
}

/** Resultado da casa se a escolha bater: positivo a casa ganha, negativo a casa perde. */
export interface ResultadoTipos {
  simples: number;
  multiplas: number;
  criarAposta: number;
  total: number;
}

export interface LiabilityEscolha {
  /** Como no bilhete ("Mais de 2,5", "Anfernee Simons (PHI) mais 11,5"). */
  escolha: string;
  simples: ApostasTipo;
  multiplas: ApostasTipo;
  criarAposta: ApostasTipo;
  total: ApostasTipo;
  /**
   * Odd média ponderada pelo apostado: nas simples, a odd de verdade; sem simples, a parte da
   * escolha na odd da múltipla (estimada). null sem aposta.
   */
  odd: number | null;
  /** Escolhas por bilhete, em média ponderada pelo apostado (o ABL da Altenar); null sem aposta. */
  abl: number | null;
  seBater: ResultadoTipos;
}

/**
 * Um mercado de um jogo com as escolhas que têm bilhete aberto. Nos mercados de linha (mais/menos),
 * cada jogador e cada linha é um mercado, e o lado sem aposta aparece com zero.
 */
export interface LiabilityMercado {
  /** Chave estável (jogo + mercado + linha). */
  id: string;
  jogo: string;
  /** Id do evento na Altenar e o resto do catálogo, pelo nome do jogo; null quando o jogo não está nele. */
  evento: string | null;
  inicio: string | null;
  esporte: string | null;
  campeonato: string | null;
  aoVivo: boolean;
  /** "Total de gols · 2,5", "Pts-Reb · Anfernee Simons (PHI) · 11,5". */
  mercado: string;
  bilhetes: number;
  apostado: number;
  /** O mercado mais apostado do jogo, como o "Most Active" da Altenar. */
  maisAtivo: boolean;
  /** Pior e melhor resultado da casa entre as escolhas do mercado. */
  pior: number;
  melhor: number;
  escolhas: LiabilityEscolha[];
}

export interface LiabilityEsportes {
  geradoEm: string;
  modo: ModoLiability;
  /** Só o mercado mais ativo de cada jogo (o padrão do relatório da Altenar). */
  soMaisAtivo: boolean;
  /** false quando o catálogo da Altenar está fora: sem horário, o começo do jogo sai das apostas. */
  catalogo: boolean;
  /** Primeiro dia (Brasília) dos bilhetes abertos considerados. */
  desde: string;
  /** Todos os bilhetes abertos, de qualquer jogo. */
  bilhetes: number;
  apostado: number;
  /** Mercados com bilhete aberto no modo (antes do corte). */
  totalMercados: number;
  /** Do pior resultado para a casa ao melhor (até 500). */
  mercados: LiabilityMercado[];
}

// ---------- /v1/saques ----------
// Sem dado pessoal: o jogador vai só pelo id.

export interface Saque {
  /** Id do saque na Evox; o gateway recebe como id externo "wd-<id>". */
  id: number;
  /** "wd-59189", como aparece no gateway. */
  idExterno: string;
  jogador: number;
  valor: number;
  /** legenda_status: 1 criado, 2 pendente, 3 pago, 4 excluído, 5 recusado, 6 expirado, 7 cancelado pelo jogador. */
  status: number;
  situacao: string;
  /** Provedor do gateway (ex.: lulipay); null quando a coluna falta ou vem vazia. */
  gateway: string | null;
  /** Id da transação no gateway, quando a Evox guarda; null quando vem vazio. */
  transacao: string | null;
  /** Instantes ISO-8601 (UTC). */
  pedidoEm: string;
  atualizadoEm: string | null;
  /**
   * Quando as regras de risco da Evox pararam o saque para revisão manual (ISO-8601, UTC).
   * null quando nunca parou ou a coluna falta. A Evox não guarda o motivo da recusa na réplica.
   */
  revisaoEm: string | null;
  /** Código cru de auto_resolution (ex.: held_gateway_unknown); null quando vem vazio ou a coluna falta. */
  resolucaoAutomatica: string | null;
  /** O código em texto do painel; o próprio código quando ainda não tem tradução. */
  resolucaoAutomaticaTexto: string | null;
  /** Quando a Evox resolveu o saque sozinha (ISO-8601, UTC); null quando não resolveu. */
  resolvidoAutomaticamenteEm: string | null;
}

export interface ListaSaques {
  saques: Saque[];
}

// ---------- /v1/jogadores/:id/depositos e /saques (pagamentos do jogador) ----------
// Os saques do jogador voltam como ListaSaques. Do depósito, a chave/código Pix (key) e o
// order_id ficam de fora.

export interface Deposito {
  /** Id do depósito na Evox. */
  id: number;
  jogador: number;
  valor: number;
  /** legenda_status: 1 criado, 2 pendente, 3 pago, 4 excluído, 5 recusado, 6 expirado. */
  status: number;
  situacao: string;
  /** Provedor do gateway (ex.: lulipay); null quando a coluna falta ou vem vazia. */
  gateway: string | null;
  /** Id da transação no gateway, quando a Evox guarda; null quando vem vazio. */
  transacao: string | null;
  /** Instantes ISO-8601 (UTC). */
  pedidoEm: string;
  atualizadoEm: string | null;
  /** Quando a cobrança expira (ou expirou); null quando a coluna falta ou vem vazia. */
  expiraEm: string | null;
}

export interface ListaDepositos {
  depositos: Deposito[];
}
